import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Instagram } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { DESSERTS_DATA, dessertSlug, FEATURED_DESSERT_IDS } from './Desserts';
import SocialLinks, { countSocialLinks, normalizeSocialUrl } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=1200&h=800&fit=crop';

function resolveMapsUrl(d: any) {
  if (d.mapsUrl && String(d.mapsUrl).trim()) return d.mapsUrl;
  const address = d.address as string | undefined;
  if (address && address.trim() && !address.trim().startsWith('@')) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
  }
  return '';
}

export default function DessertDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('desserts', DESSERTS_DATA, (d, i) =>
    slugify(d.id || d.name || String(i))
  );

  const entry = mc.items.find((d: any) => dessertSlug(d) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Dessert Shop Not Found | Dallas Desi Scoop"
          description="This dessert shop listing could not be found."
          canonical="/dessert-shops"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Dessert shop not found.</p>
        <Link
          to="/dessert-shops"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Dessert Shops
        </Link>
      </div>
    );
  }

  const isFeatured = FEATURED_DESSERT_IDS.has(entry.id) || FEATURED_DESSERT_IDS.has(dessertSlug(entry));
  const resolvedInstagramUrl =
    normalizeSocialUrl(entry.instagramUrl, "instagram") ||
    normalizeSocialUrl(entry.instagram, "instagram") ||
    normalizeSocialUrl(entry.instagram_url, "instagram");
  const hasInstagram = !!resolvedInstagramUrl;
  const resolvedMapsUrl = resolveMapsUrl(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const city = entry.city || entry.location || 'DFW';
  const title = `${entry.name} in ${city}, TX | Dessert Shop | Dallas Desi Scoop`;
  const description = `${entry.name} is a dessert shop serving the South Asian community in ${city}, TX. Find address, contact info, and social links.`;
  const canonicalPath = `/dessert-shops/${slug}`;

  const addressParts = (entry.address || '').split(',').map((p: string) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: 'Dessert shop',
      address: !entry.address?.trim()?.startsWith('@')
        ? {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: addressLocality || undefined,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      url: entry.instagramUrl || entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Dessert Shops', item: `${BASE_URL}/dessert-shops` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/dessert-shops"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Dessert Shops
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && !entry.address.trim().startsWith('@') && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            {entry.address && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">Dessert Shop</span>
              </>
            )}
          </motion.div>

          {isFeatured && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24 }}
              className="flex items-center gap-2 mt-4"
            >
              <p className="text-[12px] font-semibold text-[#51ba4f] tracking-wide">
                Featured by TastebudsByAnubhi
              </p>
              {hasAnySocial && <SocialLinks item={entry} px={22} />}
            </motion.div>
          )}
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is a dessert shop serving the South Asian community across Dallas-Fort Worth
            {entry.city ? `, located in ${entry.city}, TX` : ''}.
            {entry.address && !entry.address.trim().startsWith('@') ? ` Located at ${entry.address}.` : ''}
          </p>
          {!isFeatured && hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={20} />
            </div>
          )}
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {entry.address && !entry.address.trim().startsWith('@') && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.address}</span>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {hasInstagram && (
              <a
                href={resolvedInstagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </a>
            )}
            {!!resolvedMapsUrl && (
              <a
                href={resolvedMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Get Directions
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
